<template>
  <div>
    <div class="editor" :class="classes">
      <div class="main">
        <div>
          <router-link :to="galleryLink" class="alt-font">‹ Pattern Gallery</router-link>
        </div>
        <design-view :design="design" @block-click="onBlockClick" @block-drop="onBlockDrop" @block-drag="onBlockDrag" />
        <div v-if="showHelpNote" class="help-note">{{ helpNote }}</div>
        <div class="download-pdf">
          <download-pdf />
        </div>
        <div class="save-or-share">
          <label class="alt-font">Save or Share Design</label>
          <save-form />
        </div>
      </div>
      <div class="colors" v-if="brand === 'filzfelt'">
        <label class="alt-font">Colors</label>
        <div class="colors-container">
          <div class="color-circle-container" v-for="[key, c] in Object.entries(colors)">
            <button class="color-circle" :class="activeColor === key && 'active'" :style="`background: ${c.value}`"
              @click="setActiveColor(key)">
            </button>
            <div class="tooltip alt-font">
              <span class="tooltip-content">{{ c.idx }} {{ c.name }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="customize" v-if="!isSp402 && brand === 'spinneybeck'">
        <label class="alt-font">Customize</label>
        <div class="customize-button" v-for="cb in customBlocks">
          <button :class="activeCustomBlock === cb.id && 'active'" v-draggable @click="setActiveCustomBlock(cb)"
            @drag-start="grabCustomBlock(cb)" :data-drag="getCustomBlockData(cb)">
            <svg viewBox="0 0 16 16">
              <path :d="cb.d" />
            </svg>
          </button>
          <div class="tooltip alt-font">
            <span class="tooltip-content">{{ cb.name }}</span>
          </div>
        </div>
        <div class="customize-button">
          <button class="rotate-button" :class="activeRotate && 'active'" @click="setActiveRotate()">
          </button>
          <div class="tooltip alt-font">
            <span class="tooltip-content">Rotate</span>
          </div>
        </div>
      </div>
      <div class="sidebar">
        <div class="form-group">
          <div class="label-switch">
            <label class="alt-font">Width</label>
            <template v-if="showMetric">
              <button class="unit-switch" @click="switchUnits">Switch to {{ metric ? 'Imperial' : 'Metric' }}</button>
            </template>
          </div>
          <dimension-input :id="`${this._uid}-input-width`" :value="design.width" @input="setWidth"
            :showInches="showInches" />
        </div>
        <div class="form-group">
          <label class="alt-font">Height</label>
          <dimension-input :value="design.height" @input="setHeight" :showInches="showInches" />
          <template v-if="showPatternRepeatHeight">
            <p class="min-height-message">Pattern has a repeat of {{ patternRepeatHeight }}.</p>
          </template>
        </div>
        <div class="form-group">
          <label class="alt-font">Pattern</label>
          <select class="select" :value="design.pattern" @change="setPattern($event.target.value)">
            <option v-for="p in patterns" :value="p.id">{{ p.name }}</option>
          </select>
        </div>
        <div class="form-group" v-if="brand === 'filzfelt'">
          <div class="apply-color-container">
            <label class="alt-font">Apply Color</label>
            <div class="tooltip apply-color alt-font">
              <span class="tooltip-content">Select ‘Single’ to add color to individual tiles or ‘All’ to add one color
                to entire wall. To switch color of a tile, selection ‘Single’, click on another color, and then click on
                that tile.
              </span>
            </div>
          </div>
          <div class="button-group">
            <button class="button" type="button" @click="clearColorForAll()">Clear</button>
            <template v-if="showFill">
              <button class="button" type="button" @click="activeColor && setColorForAll(activeColor)">Fill</button>
            </template>
          </div>
        </div>
        <div class="form-group">
          <router-link class="alt-font" to="view">View Specs</router-link>
        </div>
        <div class="form-group">
          <div class="need-help-container">
            <a href="javascript:void(0)" class="alt-font">Need Help?</a>
            <div class="tooltip how-to">
              <div class="tooltip-content alt-font">
                <ol>
                  <li v-for="step in howToText" v-html="step"></li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="ghost-block" v-if="ghostBlock" :style="`top: ${cursorY - 25}px; left: ${cursorX - 25}px`">
      <svg viewBox="0 0 16 16">
        <path :d="ghostBlock.d" :transform="`rotate(${ghostBlock.r || 0} 8 8)`" />
      </svg>
    </div>
  </div>
</template>

<script>
import DesignView from '../components/DesignView'
import DimensionInput from '../components/DimensionInput'
import DownloadPdf from '../components/DownloadPdf'
import SaveForm from '../components/SaveForm'
import draggable from '../lib/v-draggable'
import colors from '../../universal/filzfelt/colors'
import ffPatterns from '../lib/filzfelt/patterns'
import ffPatternDefs from '../../universal/filzfelt/patterns'
import spPatterns from '../lib/spinneybeck/patterns'
import spPatternDefs from '../../universal/spinneybeck/patterns'
import spBlocks from '../../universal/spinneybeck/blocks'
import ffBlocks from '../../universal/filzfelt/blocks'
import contstants from '../lib/constants';
import { convertInchesToCm, convertInchesToFeet } from '../lib/conversions';

export default {
  components: {
    DesignView,
    DimensionInput,
    DownloadPdf,
    SaveForm
  },
  directives: {
    draggable
  },
  data() {
    return {
      colors,
      activeColor: false,
      activeCustomBlock: false,
      activeRotate: false,
      draggedBlock: null,
      cursorX: 0,
      cursorY: 0
    }
  },
  computed: {
    design() {
      return this.$store.getters.design
    },
    brand() {
      return this.$store.getters.brand
    },
    metric() {
      return this.$store.getters.metric
    },
    patterns() {
      return this.brand === 'filzfelt'
        ? ffPatterns
        : spPatterns
    },
    patternDefs() {
      return this.brand === 'filzfelt'
        ? ffPatternDefs
        : spPatternDefs
    },
    patternDef() {
      return this.patternDefs[this.design.pattern];
    },
    helpNote() {
      if (!this.patternDef) return false;
      return this.patternDef.customHelpNote ? this.patternDef.customHelpNote : '';
    },
    showHelpNote() {
      if (!this.patternDef) return false;
      return this.patternDef.customHelpNote ? true : false;
    },
    showMetric() {
      if (!this.patternDef) return true;
      return this.patternDef.showMetric === false ? this.patternDef.showMetric : true;
    },
    showInches() {
      if (!this.patternDef) return true;
      return this.patternDef.showInches === false ? this.patternDef.showInches : true;
    },
    showFill() {
      if (!this.patternDef) return true;
      return this.patternDef.showFill === false ? this.patternDef.showFill : true;
    },
    showPatternRepeatHeight() {
      if (!this.patternDef || !this.patternDef.patternRepeatHeight) return false;
      if (this.metric) {
        return this.design.height < convertInchesToCm(this.patternDef.patternRepeatHeight);
      }
      return this.design.height < this.patternDef.patternRepeatHeight;
    },
    patternRepeatHeight() {
      if (!this.patternDef || !this.patternDef.patternRepeatHeight) return;
      if (this.metric) {
        return `${convertInchesToCm(this.patternDef.patternRepeatHeight)}cm`;
      }
      const feetInches = convertInchesToFeet(this.patternDef.patternRepeatHeight);
      return `${feetInches.feet} '${feetInches.inches !== 0 ? ` ${feetInches.inches} "` : ''}`;
    },
    isSp402() {
      const pattern = spPatternDefs[this.design.pattern]
      return /402/.test(pattern && pattern.name)
    },
    customBlocks() {
      const pattern = spPatternDefs[this.design.pattern]
      if (!pattern || !pattern.customBlocks) return []
      return pattern.customBlocks.map(id => ({ id, ...spBlocks[id] }))
    },
    ghostBlock() {
      if (!this.draggedBlock) return
      const { id, r } = this.draggedBlock
      const d = spBlocks[id].d
      return { id, r, d }
    },
    classes() {
      return {
        'active-color': this.activeColor,
        'active-custom-block': this.activeCustomBlock,
        'active-rotate': this.activeRotate,
        'drag-over': this.draggedBlock
      }
    },
    galleryLink() {
      const brand = this.brand
      return { name: 'intro', params: { brand } }
    },
    howToText() {
      if (this.isSp402) {
        return [
          'Enter the width and height of your wall.',
          'To see the tile counts, click on ‘View Specs’.',
          'To save or share your work, enter your email address in the lower left corner. You’ll receive an email with a link to pick up where you left off.',
          'To download a PDF of your design, click on ‘Download PDF’.'
        ]
      }

      if (this.brand === 'filzfelt') {
        return [
          'Enter the width and height of your wall.',
          'To add color to individual tiles, select a color and click on tiles. To remove color, click again on that tile. Click ‘Fill’ to fill entire wall with selected color.',
          'Continue adding color by selecting colors and clicking on tiles until your design is complete.',
          'Want a blank slate? Click ‘Clear’.',
          'To create a floating installation (instead of wall-to- wall), leave tiles blank.',
          'To see the tile counts, click on ‘View Specs’.',
          'To save or share your work, enter your email address in the lower left corner. You’ll receive an email with a link to pick up where you left off.',
          'To download a PDF of your design, click on ‘Download PDF’.'
        ]
      }
      // Spinneybeck
      return [
        'Enter the width and height of your wall.',
        'To customize, select a tile style under ‘Customize’ and drag to the location on your wall.',
        'To rotate, select ‘Rotate’ and then click on a tile to rotate.',
        'To see the tile counts, click on ‘View Specs’.',
        'To save or share your work, enter your email address in the lower left corner. You’ll receive an email with a link to pick up where you left off.',
        'To download a PDF of your design, click on ‘Download PDF’.'
      ]
    }
  },

  methods: {
    onBlockClick({ block, key }) {
      if (this.brand === 'filzfelt' && this.activeColor) {
        // if fill all is true, set color for all blocks of same type
        if (ffBlocks[block.id].fillAll) {
          this.setColorForSameBlockType({ key, color: this.activeColor })
          return;
        }
        this.setColor({ key, color: this.activeColor })
      }
      if (this.brand === 'spinneybeck' && this.activeCustomBlock) {
        const id = this.activeCustomBlock
        const name = spBlocks[id].name || ''
        const r = 0
        this.setCustomBlock({ key, id, name, r })
      }
      if (this.brand === 'spinneybeck' && this.activeRotate) {
        let r = this.design.data[key].r || 0
        r = (r + 90) % 360
        this.setBlockRotate({ key, r })
      }
    },
    onBlockDrop({ key, id, name, r }) {
      this.draggedBlock = null
      this.setCustomBlock({ key, id, name, r })
    },
    onBlockDrag({ id, name, r }) {
      this.draggedBlock = { id, name, r }
    },
    grabCustomBlock(cb) {
      const { id } = cb
      this.draggedBlock = { id }
      this.setActiveCustomBlock(id)
    },
    getCustomBlockData({ id, name }) {
      return JSON.stringify({ id, name })
    },
    setActiveColor(key) {
      this.activeCustomBlock = false
      this.activeRotate = false
      this.activeColor = key
    },
    setActiveCustomBlock(cb) {
      const { id } = cb
      this.activeColor = false
      this.activeRotate = false
      this.activeCustomBlock = id
    },
    setActiveRotate() {
      this.activeCustomBlock = false
      this.activeColor = false
      this.activeRotate = true
    },
    setWidth(value) {
      if (this.design.width === value || isNaN(value) || !value) {
        return
      }

      if (this.metric) {
        // convert cm to inches
        this.$store.commit('setWidth', Math.round(value / 2.54))
      } else {
        this.$store.commit('setWidth', value)
      }
    },
    setHeight(value) {
      if (this.design.height === value || isNaN(value) || !value) {
        return
      }

      if (this.metric) {
        // convert cm to inches
        this.$store.commit('setHeight', Math.round(value / 2.54))
      } else {
        this.$store.commit('setHeight', value)
      }
    },
    setInitialHeight() {
      const patternHeight = this.patternDef.wallHeight;
      if (patternHeight) {
        this.$store.commit('setHeight', patternHeight);
      } else {
        this.$store.commit('setHeight', contstants.DEFAULT_WALL_HEIGHT);
      }
    },
    setInitialWidth() {
      const patternWidth = this.patternDef.wallWidth;
      if (patternWidth) {
        this.$store.commit('setWidth', patternWidth);
      } else {
        this.$store.commit('setWidth', contstants.DEFAULT_WALL_WIDTH);
      }
    },
    setPattern(id) {
      this.$store.dispatch('setPatternById', id)
    },
    setColor(payload) {
      this.$store.commit('setColor', payload)
    },
    setColorForSameBlockType(payload) {
      this.$store.commit('setColorForSameBlockType', payload)
    },
    setColorForAll(payload) {
      this.$store.commit('setColorForAll', payload)
    },
    clearColorForAll(payload) {
      this.$store.commit('clearColorForAll', payload)
    },
    setCustomBlock(payload) {
      this.$store.commit('setCustomBlock', payload)
    },
    setBlockRotate(payload) {
      this.$store.commit('setBlockRotate', payload)
    },
    switchUnits() {
      this.$store.commit('setMetric', !this.metric)
    }
  },

  created() {
    if (!this.design.pattern) {
      this.setPattern(this.brand === 'filzfelt' ? ffPatterns[0].id : spPatterns[0].id)
    }
  },

  mounted() {
    window.scrollTo(0, 0)
    window.addEventListener('mousemove', event => {
      this.cursorX = event.pageX
      this.cursorY = event.pageY
    })
    window.addEventListener('mouseup', event => {
      this.draggedBlock = null
    })

    this.setInitialWidth();
    this.setInitialHeight();
  }
}
</script>

<style lang="postcss" scoped>
@import "../styles/vars.css";

.editor {
  display: flex;
  margin: 0 -20px 30px;
  min-height: 650px;

  &>* {
    margin: 0 20px;
  }
}

.main {
  flex: 1 0 300px;
}

.sidebar {
  flex: 0 0 170px;
}

label {
  display: block;
}

.label-switch {
  display: flex;
}

.min-height-message {
  font-size: 0.75rem;
  color: #999;
  line-height: 1.2;
}

.unit-switch {
  display: block;
  border: none;
  margin: 0 0 0 auto;
  padding: 0 0.2em;
  font-size: 0.75rem;
  cursor: pointer;
}

.select {
  display: block;
  width: 100%;
  border: solid 2px #aeaeae;
  height: 45px;
  padding-left: 1em;
  -moz-appearance: none;
  -webkit-appearance: none;
  background: url(../assets/drop.png) no-repeat right 0.7em center;
}

.button {
  display: block;
  width: 100%;
  border: solid 2px #aeaeae;
  height: 45px;
}

.form-group {
  margin-bottom: 15px;
}

.button-group {
  display: flex;
  padding-left: 2px;

  &>* {
    flex: 1 1 auto;
    margin-left: -2px;
  }
}

.colors {
  flex: 0 0 170px;
}

.colors-container {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
}

.color-circle {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border: solid 2px #fff;
  border-radius: 30px;
  margin: 2px;
  cursor: pointer;

  &.active {
    border: solid 2px #999;
  }
}

.color-circle-container,
.need-help-container,
.apply-color-container {
  position: relative;
}

.tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  margin-left: -100px;
  margin-bottom: 7px;
  width: 200px;
  visibility: hidden;
  pointer-events: none;
  text-align: center;
}

*:hover>.tooltip {
  visibility: visible;
}

*:focus+.tooltip {
  visibility: visible;
}

.tooltip-content {
  border-radius: 0.3em;
  padding: 0.5em 0.7em;
  background: #999;
  color: #fff;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border: 5px solid transparent;
    border-top-color: #999;
  }
}

.tooltip.how-to,
.tooltip.apply-color {
  background: #999;
  left: auto;
  right: 0;
  width: 440px;
  margin: 0;
  text-align: left;
}

.tooltip.how-to .tooltip-content,
.tooltip.apply-color .tooltip-content {
  display: block;
}

.tooltip.how-to .tooltip-content:after,
.tooltip.apply-color .tooltip-content:after {
  left: 70%;
}

.tooltip.how-to .tooltip-content ol,
.tooltip.apply-color .tooltip-content ol {
  margin: 0.5rem 1.5rem;
  padding: 0;
}

.customize {
  & .customize-button {
    position: relative;
  }

  & button {
    display: block;
    width: 60px;
    height: 60px;
    border: solid 1px var(--color-main);
    margin: 0 auto 10px auto;
    padding: 0;

    &.active {
      border: solid 3px var(--color-main);
    }
  }

  & svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  & rect {
    fill: none;
    stroke: var(--color-main);
    stroke-width: 0.6;
  }

  & path {
    fill: none;
    stroke: var(--color-main);
    stroke-width: 0.4;
  }

  & .rotate-button {
    background-size: 30px 30px;
    background: url("../assets/rotate.svg") no-repeat 50%;
  }

}

.help-note {
  line-height: 1.2;
  margin-top: 10px;
  font-size: 0.85rem;
  font-style: italic;
  max-width: 760px;
}

.download-pdf,
.save-or-share {
  margin-top: 30px;
}

.gallery {
  margin: 45px 0;
  border-top: 2px dotted #929496;
}

.ghost-block {
  position: absolute;
  width: 50px;
  height: 50px;
  border: solid 1px var(--color-main);
  background: #fff;
  pointer-events: none;

  & svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  & path {
    fill: none;
    stroke: var(--color-main);
    stroke-width: 0.4;
  }
}
</style>



// WEBPACK FOOTER //
// Edit.vue?0eb5260d